"use client";

import { cn } from "@/lib/utils";
import { animate, motion, useMotionValue } from "framer-motion";
import {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

interface AiModeOutlineProps {
  children?: ReactNode;
  borderWidth?: number;
  animationDuration?: number;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  gradient?: string;
  className?: string;
  disabled?: boolean;
}

export interface AiModeOutlineRef {
  triggerAnimation: () => Promise<void>;
  stopAnimation: () => void;
}

const defaultGradient = `conic-gradient(
  rgba(255,178,77,0) 10deg,
  rgba(255,178,77,1) 30deg,
  rgba(255,107,157,1) 50deg,
  rgba(169,0,183,1) 70deg,
  rgba(66,153,225,1) 90deg,
  rgba(56,178,172,1) 110deg,
  rgba(255,71,87,1) 130deg,
  rgba(255,178,77,0) 150deg,
  rgba(255,178,77,0) 360deg
)`;

const AiModeOutline = forwardRef<AiModeOutlineRef, AiModeOutlineProps>(
  (
    {
      children,
      borderWidth = 2,
      animationDuration = 3,
      fadeInDuration = 0.3,
      fadeOutDuration = 0.5,
      gradient = defaultGradient,
      className,
      disabled = false,
    },
    ref,
  ) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const opacity = useMotionValue(0);
    const rotation = useMotionValue(0);
    const rotationControlsRef = useRef<any>(null);
    const opacityControlsRef = useRef<any>(null);

    // Measure container size
    useEffect(() => {
      const measureContainer = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerSize({ width: rect.width, height: rect.height });
        }
      };

      measureContainer();

      const resizeObserver = new ResizeObserver(measureContainer);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // Calculate gradient size (2x the largest side)
    const maxSide = Math.max(containerSize.width, containerSize.height);
    const gradientSize = maxSide * 2;

    useImperativeHandle(ref, () => ({
      triggerAnimation: async () => {
        if (disabled || isAnimating) return;

        setIsAnimating(true);

        // Reset rotation to start fresh
        rotation.set(0);

        // Start fade in
        opacityControlsRef.current = animate(opacity, 1, {
          duration: fadeInDuration,
          ease: "easeOut",
        });

        // Start continuous rotation animation
        rotationControlsRef.current = animate(rotation, 360, {
          duration: animationDuration,
          ease: "linear",
          repeat: Infinity,
        });
      },

      stopAnimation: () => {
        if (!isAnimating) return;

        // Stop fade in if still running, but keep rotation going
        if (opacityControlsRef.current) {
          opacityControlsRef.current.stop();
        }

        // Start fade out while rotation continues
        animate(opacity, 0, {
          duration: fadeOutDuration,
          ease: "easeIn",
        }).then(() => {
          // Only now stop the continuous rotation after fade-out completes
          if (rotationControlsRef.current) {
            rotationControlsRef.current.stop();
          }

          setIsAnimating(false);
          rotation.set(0);
        });
      },
    }));

    if (disabled) {
      return <>{children}</>;
    }

    return (
      <div ref={containerRef} className={cn("relative rounded-lg", className)}>
        <div className="relative z-1">{children}</div>

        {/* Animated outline container */}
        <motion.div
          className="pointer-events-none absolute overflow-hidden rounded-[inherit]"
          style={{
            opacity,
            inset: `-${borderWidth}px`,
          }}
        >
          {/* Large rotating gradient background */}
          <motion.div
            className="absolute rounded-[inherit]"
            style={{
              background: gradient,
              width: `${gradientSize}px`,
              height: `${gradientSize}px`,
              left: `50%`,
              top: `50%`,
              x: "-50%",
              y: "-50%",
              rotate: rotation,
              contain: "paint",
            }}
          />
        </motion.div>
      </div>
    );
  },
);

AiModeOutline.displayName = "AiModeOutline";

export { AiModeOutline };
